import {createAction, createReducer} from 'redux-act';
import {AxiosPromise} from 'axios';
import {FULFILLED, PENDING, REJECTED} from 'redux-promise-middleware';

import {API_URL} from 'constants/api';
import {doTransform} from 'libs/transformers';
import {ENDPOINTS} from 'constants/endpoints';
import {ApplicationStoreType} from 'reducers';
import {LOGIN, REGISTER, FORGOT} from 'features/menu/constants';
import {instanceGusaBackend} from 'libs/api';

import {OAuthType, OAuthSocialType, CustomerType} from './oAuthType';

export const initialState: OAuthType = {
    htoExperience: false,
    id: 0,
    createdAt: '',
    email: '',
    firstname: '',
    lastname: '',
    middlename: null,
    genderId: 0,
    addresses: [],
    group: {
        id: 0,
        title: '',
    },
    newsletter: false,
    socialLogins: [],
    prescriptions: [],
    billingAddressId: 0,
    shippingAddressId: 0,
    isLoggedIn: false,
    isLoading: true,
    md5: '',
    sha1: '',
    sha256: '',
    error: '',
    redirect: '',
    authOption: LOGIN,
    notificationMessage: '',
};

/**
 * Action that inits getting customer token
 * @param {string} email - Customer email
 * @param {string} password - Customer password
 * @param {'login' | 'signup'} type - login of signup temporary field for analytics to define if it was login or signup (will be deleted after register returns token)
 * @return {{email: string, password: string, type: 'login' | 'signup'}} action with email and password values
 */
export const getToken = createAction<{email: string; password: string; type: 'login' | 'signup'}>(
    'GET_TOKEN',
    (email: string, password: string, type: 'login' | 'signup') => ({
        email,
        password,
        type,
    }),
);
/**
 * Sets error message
 */
export const setError = createAction<string>('SET_TOKEN_ERROR');
/**
 * Action that inits getting customer token via socials
 */
export const getSocialToken = createAction<OAuthSocialType>('SET_TOKEN_SOCIAL');
/**
 * Send request for getting logged customer data
 */
export const fetchSelf = createAction<AxiosPromise<CustomerType>>('FETCH_SELF', () =>
    instanceGusaBackend({
        method: 'get',
        baseURL: API_URL,
        url: `${ENDPOINTS.CUSTOMER_SELF}`,
    }),
);
/**
 * Sets redirect after success auth/registration
 */
export const setRedirect = createAction<string>('SET_REDIRECT_AFTER_SUCCESS_AUTH');
export const setNotificationMessage = createAction<string>('SET_AUTH_NOTIFICATION_MESSAGE');

export const setAuthOption = createAction<LOGIN | REGISTER | FORGOT>('SET_AUTH_OPTION');

/**
 * OAuth reducer
 */
export const oAuth = createReducer<OAuthType>({}, initialState)
    .on(
        `${setError}`,
        (state: OAuthType, payload: string): OAuthType => ({
            ...state,
            error: payload,
        }),
    )
    .on(
        `${fetchSelf}_${PENDING}`,
        (state: OAuthType): OAuthType => ({
            ...state,
            error: '',
            isLoading: true,
        }),
    )
    .on(
        `${fetchSelf}_${REJECTED}`,
        (
            state: OAuthType,
            {
                response: {
                    data: {message = ''},
                },
            },
        ): OAuthType => ({
            ...state,
            error: message,
            isLoading: false,
        }),
    )
    .on(
        `${fetchSelf}_${FULFILLED}`,
        (state: OAuthType, payload: {data: CustomerType}): OAuthType => ({
            ...state,
            ...payload.data,
            md5: doTransform(payload.data.email, 'md5'),
            sha1: doTransform(payload.data.email, 'sha1'),
            sha256: doTransform(payload.data.email, 'sha256'),
            isLoggedIn: true,
            isLoading: false,
        }),
    )
    .on(`${setRedirect}`, (state: OAuthType, payload: string) => ({
        ...state,
        redirect: payload,
    }))
    .on(`${setNotificationMessage}`, (state: OAuthType, payload: string) => ({
        ...state,
        notificationMessage: payload,
    }))
    .on(`${setAuthOption}`, (state: OAuthType, payload: LOGIN | FORGOT | REGISTER) => ({
        ...state,
        authOption: payload,
    }));

/**
 * Return customer data
 *
 * @param {ApplicationStoreType} store - redux store
 * @returns {OAuthType} - customer
 */
export const getCustomer = (store: ApplicationStoreType) => store.oAuth;

/**
 * Return auth option
 * @param {ApplicationStoreType} store - redux store
 * @returns {LOGIN | FORGOT | REGISTER} - auth option
 */
export const getAuthOption = (store: ApplicationStoreType) => store.oAuth.authOption;

/**
 * Get true value if user is logged in or not
 * @param {ApplicationStoreType} store - Application store
 * @return {boolean} OAuth customer is logged ib or not
 */
export const getIsLoggedIn = (store: ApplicationStoreType) => store.oAuth.isLoggedIn;
